import { Flex, Text } from "@/atoms"
import { EVENTS } from "@/const/event.constants"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTooltip } from "@/hooks/useTooltip"
import { useUser } from "@/hooks/useUser"
import { PartitionOutlined } from "@/icons"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { CreateActionEmptyTable } from "@/molecules/EmptyTable"
import { LinkButton } from "@/molecules/LinkButton"
import { Protected } from "@/molecules/Protected"
import { ActionsOverTimeWidget } from "@/pages/GovernancePage/components/organisms/ActionsOverTimeWidget"
import { ActionsTakenWidget } from "@/pages/GovernancePage/components/organisms/ActionsTakenWidget"
import { Filter } from "@/pages/SettingsPage/components/organisms/Filter"
import { TabTemplate } from "@/templates/TabTemplate"
import { getColumnsTable } from "./getColumnsTable"

import styles from "./AutomatedActionsTab.module.scss"

import type { Workflow } from "@/api/useWorkflow.types"
import type { Action, Resource } from "@/domain/user"
import { InformationAlert } from "@/molecules/InformationAlert"
import { DraggingRow } from "../DraggableTable"
import type { AutomatedActionsTabProps } from "./AutomatedActionsTab.types"

export const AutomatedActionsTab = (props: AutomatedActionsTabProps) => {
  const { ROOT, WORKFLOWS } = NAMES_ROUTES.GOVERNANCE
  const { draggableDataSource: data, actions, loading, t } = props

  const { confirm } = useConfirmModal()
  const { hasPermission } = useUser()
  const resource = "governance.workflows" as Resource
  const permissions = { resource, action: "write" as Action }
  const noPermissions = !hasPermission(permissions)

  const hasError = data?.some((workflow) => workflow.isError)

  const onDelete = async (record: Pick<Workflow, "id" | "name">) => {
    const { name } = record

    confirm((attr) =>
      DeleteConfirmModal({
        name,
        ANALYTIC_EVENT: EVENTS.ANALYTIC_EVENTS.WORKFLOW.DELETE,
        ANALYTIC_TAGS: { id: record.id },
        entityName: "workflow",
        onOk: () => actions.deletion.mutateAsync(record.id),
        ...attr
      })
    )
  }

  useTooltip(data)

  const createUrl = `/${ROOT}/${WORKFLOWS}/new`
  const convertors = useNumberConvertor()

  return (
    <TabTemplate
      useAutoEmptyStateHeight
      resizable
      columns={getColumnsTable({
        noPermissions,
        onDelete,
        convertors,
        ...actions
      })}
      loading={loading}
      dataSource={data}
      classes={{ row: styles.container }}
      components={{ body: { row: DraggingRow } }}
      headerComponent={
        <Flex vertical gap={14} className="sticky-section-tab-template-ks">
          <Flex justify="space-between" align="center">
            <Text type="secondary">
              {t("settings.automatedActions.table.description")}
            </Text>
            <Protected shouldDisable permission={permissions}>
              <LinkButton
                size="middle"
                type="primary"
                to={createUrl}
                icon={<PartitionOutlined />}
              >
                {t("settings.automatedActions.table.actions.create")}
              </LinkButton>
            </Protected>
          </Flex>
          <Flex vertical gap={8}>
            <ActionsTakenWidget />
            <ActionsOverTimeWidget />
          </Flex>
        </Flex>
      }
      SearchComponent={
        <div className={styles.searchComponentContainer}>
          <Filter />
          <InformationAlert
            message={t("settings.automatedActions.table.note")}
          />
          {hasError && (
            <InformationAlert
              message={t("settings.automatedActions.table.errorAlert")}
              type="error"
            />
          )}
        </div>
      }
      onRowClick={actions.onEdit}
      locale={{
        customEmptyComponent: (
          <Protected shouldDisable permission={permissions}>
            <CreateActionEmptyTable tKey="workflow" to={createUrl} />
          </Protected>
        )
      }}
    />
  )
}
